import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { getQueryStringValue } from './gtmEventTypeHelper';

export const SKILL_ENGAGEMENT_EVENT = 'custEv_skillEngagement' as const;
export const PLUGIN_ENGAGEMENT_EVENT = 'custEv_pluginEngagement' as const;
export const AI_ASSET_SEARCH_EVENT = 'custEv_search' as const;
export const SKILL_ENGAGEMENT_METRIC = 'skill_engagements' as const;

export type AiAssetKind = 'skill' | 'plugin';
export type AiAssetInstallTab = 'AI Prompt' | 'CLI Script' | 'ZIP Download' | 'External';
export type AiAssetInstallMethod = 'Agentforce Vibes' | 'Claude Code' | 'Cursor' | 'Codex' | 'OpenCode';
export type AiAssetSearchCategory = 'files' | 'package components';

export const INSTALL_METHOD_LABELS = {
    'agentforce-vibes': 'Agentforce Vibes',
    'claude-code': 'Claude Code',
    cursor: 'Cursor',
    codex: 'Codex',
    opencode: 'OpenCode',
} as const satisfies Record<string, AiAssetInstallMethod>;

// This module owns analytics for the Skill and Plugin listing types (AI Assets), not
// the broader AppExchange marketplace or the separate Marketing Analytics pipeline.
// GA4 field names and values below are external wire contracts and intentionally remain
// byte-for-byte unchanged by the internal naming boundary.
export interface AiAssetEngagementContext {
    kind: 'engagement';
    aiAssetKind?: AiAssetKind;
    clickText: string;
    clickUrl?: string;
    elementTitle: string;
    elementType: string;
    installationTab?: AiAssetInstallTab;
    installationMethodId?: keyof typeof INSTALL_METHOD_LABELS;
    position?: number;
    pluginResults?: number;
    skillResults?: number;
}

export interface AiAssetSearchContext {
    kind: 'search';
    aiAssetKind?: AiAssetKind;
    searchTerm: string;
    searchResultCount: number;
    searchCategory: AiAssetSearchCategory;
}

export type AiAssetAnalyticsContext = AiAssetEngagementContext | AiAssetSearchContext;

export interface AiAssetSourceEvent {
    topic: string;
    aiAssetAnalytics?: AiAssetAnalyticsContext;
    external_id?: string;
    oafId?: string;
    title?: string;
    url?: string;
    publisher?: string;
    pricing?: string;
    recommended?: boolean;
    sponsoredCampaign?: unknown;
    marketplaceListedOn?: string;
    marketplaceLastUpdated?: string;
}

export interface GA4AiAssetEngagementEvent {
    event: typeof SKILL_ENGAGEMENT_EVENT | typeof PLUGIN_ENGAGEMENT_EVENT;
    click_text: string;
    click_url: string | undefined;
    element_title: string;
    element_type: string;
    internal_driver: string | undefined;
    featured: boolean;
    recommended: boolean;
    listing_id: string;
    listing_name: string;
    listing_type: 'AI - Skill' | 'AI - Plugin';
    listing_url: string;
    listing_last_updated: string | undefined;
    listing_listed_on: string | undefined;
    page_type: 'skills page' | 'plugins page';
    solution_type: AiAssetKind;
    solution_provider: string;
    solution_cost: 'Free';
    installation_tab: AiAssetInstallTab | undefined;
    installation_method: AiAssetInstallMethod | undefined;
    is_beta: true;
    position?: number;
    plugin_results?: number;
    skill_results?: number;
}

export interface GA4AiAssetSearchEvent {
    event: typeof AI_ASSET_SEARCH_EVENT;
    search_term: string;
    search_type: 'skill search' | 'plugin search';
    search_result_count: number;
    search_category: AiAssetSearchCategory;
}

export function aiAssetEngagement(values: Omit<AiAssetEngagementContext, 'kind'>): AiAssetEngagementContext {
    return { kind: 'engagement', ...values };
}

export function aiAssetSearch(values: Omit<AiAssetSearchContext, 'kind'>): AiAssetSearchContext {
    return { kind: 'search', ...values };
}

export function trimToUndefined(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed || undefined;
}

export function normalizeAiAssetListingDate(value: unknown): string | undefined {
    const candidate = trimToUndefined(value);
    if (!candidate) {
        return undefined;
    }
    const parsed = new Date(candidate);
    if (Number.isNaN(parsed.getTime())) {
        return undefined;
    }
    return parsed.toISOString().slice(0, 10);
}

export function isAiAssetEngagementEvent(event: AiAssetSourceEvent, kind: AiAssetKind): boolean {
    return (
        event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
        event.aiAssetAnalytics?.kind === 'engagement' &&
        event.aiAssetAnalytics.aiAssetKind === kind
    );
}

export function buildAiAssetEngagement(event: AiAssetSourceEvent, kind: AiAssetKind): GA4AiAssetEngagementEvent {
    const context = event.aiAssetAnalytics as AiAssetEngagementContext;
    const clickUrl = trimToUndefined(context.clickUrl);
    return {
        event: kind === 'skill' ? SKILL_ENGAGEMENT_EVENT : PLUGIN_ENGAGEMENT_EVENT,
        click_text: trimToUndefined(context.clickText) ?? '',
        click_url: clickUrl,
        element_title: trimToUndefined(context.elementTitle) ?? '',
        element_type: trimToUndefined(context.elementType) ?? '',
        internal_driver: trimToUndefined(getQueryStringValue(clickUrl ?? '', 'd')),
        featured: Boolean(event.sponsoredCampaign),
        recommended: Boolean(event.recommended),
        listing_id: trimToUndefined(event.external_id) ?? trimToUndefined(event.oafId) ?? '',
        listing_name: trimToUndefined(event.title) ?? '',
        listing_type: kind === 'skill' ? 'AI - Skill' : 'AI - Plugin',
        listing_url: trimToUndefined(event.url) ?? '',
        listing_last_updated: normalizeAiAssetListingDate(event.marketplaceLastUpdated),
        listing_listed_on: normalizeAiAssetListingDate(event.marketplaceListedOn),
        page_type: kind === 'skill' ? 'skills page' : 'plugins page',
        solution_type: kind,
        solution_provider: trimToUndefined(event.publisher) ?? '',
        // Skills and Plugins are free AI Assets and do not carry the marketplace pricing object.
        // The generic getAppCost helper treats a missing price as paid, so the focused contract
        // must encode the AI Asset invariant instead of inferring cost from absent source data.
        solution_cost: 'Free',
        installation_tab: context.installationTab,
        installation_method: context.installationMethodId
            ? INSTALL_METHOD_LABELS[context.installationMethodId]
            : undefined,
        is_beta: true,
        ...(typeof context.position === 'number' ? { position: context.position } : {}),
        ...(typeof context.pluginResults === 'number' ? { plugin_results: context.pluginResults } : {}),
        ...(typeof context.skillResults === 'number' ? { skill_results: context.skillResults } : {}),
    };
}

export function buildAiAssetSearch(event: AiAssetSourceEvent): GA4AiAssetSearchEvent {
    const context = event.aiAssetAnalytics as AiAssetSearchContext;
    return {
        event: AI_ASSET_SEARCH_EVENT,
        search_term: trimToUndefined(context.searchTerm) ?? '',
        search_type: context.aiAssetKind === 'plugin' ? 'plugin search' : 'skill search',
        search_result_count: context.searchResultCount,
        search_category: context.searchCategory,
    };
}

export function isValidAiAssetEngagement(event: GA4AiAssetEngagementEvent): boolean {
    return getInvalidAiAssetEngagementFields(event).length === 0;
}

export function getInvalidAiAssetEngagementFields(event: GA4AiAssetEngagementEvent): string[] {
    // The settled wire contract requires both listing dates on specialized events. Missing or invalid
    // dates are reported by the handler as invalid fields; GoogleTagManager skips the dataLayer push,
    // emits one structured warning, and does not fall through to a generic GA4 event.
    const requiredStringFields = [
        'click_text',
        'element_title',
        'element_type',
        'listing_id',
        'listing_name',
        'listing_url',
        'listing_last_updated',
        'listing_listed_on',
        'solution_provider',
    ] as const;
    const invalidFields: string[] = requiredStringFields.filter((field) => !trimToUndefined(event[field]));

    if (typeof event.featured !== 'boolean') {
        invalidFields.push('featured');
    }
    if (typeof event.recommended !== 'boolean') {
        invalidFields.push('recommended');
    }
    if (event.is_beta !== true) {
        invalidFields.push('is_beta');
    }
    if (event.position !== undefined && (!Number.isInteger(event.position) || event.position <= 0)) {
        invalidFields.push('position');
    }
    if (event.plugin_results !== undefined && (!Number.isInteger(event.plugin_results) || event.plugin_results < 0)) {
        invalidFields.push('plugin_results');
    }
    if (event.skill_results !== undefined && (!Number.isInteger(event.skill_results) || event.skill_results < 0)) {
        invalidFields.push('skill_results');
    }

    return invalidFields;
}

export function isValidAiAssetSearch(event: GA4AiAssetSearchEvent): boolean {
    return (
        Boolean(trimToUndefined(event.search_term)) &&
        Number.isInteger(event.search_result_count) &&
        event.search_result_count >= 0
    );
}
